import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { monthlyCost } from '../../utils/orgPricing';

/*
 * OrganizationLayout — team pages shell: header with workspace summary and
 * tabs across the top (Members, Team tasks, Billing, Settings), wide content
 * area below. Same structure on desktop and mobile (tabs scroll on phones).
 */

const TABS = [
  { key: 'members', label: 'Members', path: '/organization/members' },
  { key: 'tasks', label: 'Team tasks', path: '/organization/tasks' },
  { key: 'billing', label: 'Billing', path: '/organization/billing', manageOnly: true },
  { key: 'settings', label: 'Settings', path: '/organization/settings', manageOnly: true },
];

const tabClass = (active) =>
  `whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
    active
      ? 'border-indigo-600 text-indigo-700'
      : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
  }`;

const CostPill = ({ org }) => (
  <span
    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-xs text-indigo-900"
    data-testid="org-header-cost"
  >
    <strong>{org.seat_count} seat{org.seat_count === 1 ? '' : 's'}</strong>
    <span className="opacity-80">
      · {org.subscription_status === 'trialing' ? 'Free during trial' : `~$${monthlyCost(org.seat_count)}/month`}
    </span>
  </span>
);

const OrganizationLayout = ({ org, active, canManage = false, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const orgQuery = org?.id ? `?org_id=${org.id}` : '';
  const orgInitial = (org?.name || '?').charAt(0).toUpperCase();
  const tabs = TABS.filter((tab) => !tab.manageOnly || canManage);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="org-layout">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-5">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1 mb-4"
            data-testid="org-back-to-tasks"
          >
            ← Back to Tasks
          </button>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center font-semibold flex-shrink-0">
              {orgInitial}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-lg font-semibold text-gray-900 truncate" title={org?.name} data-testid="org-header-name">
                {org?.name || 'Workspace'}
              </p>
              {org && <p className="text-xs text-gray-500 capitalize">{org.subscription_status}</p>}
            </div>
            {org && <CostPill org={org} />}
          </div>
          <nav className="flex gap-1 overflow-x-auto -mb-px" role="tablist" data-testid="org-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => navigate(`${tab.path}${orgQuery}`)}
                className={tabClass(active === tab.key)}
                role="tab"
                aria-selected={active === tab.key}
                data-testid={`org-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 md:px-8 md:py-8" key={location.pathname}>
        {children}
      </main>
    </div>
  );
};

export default OrganizationLayout;
